import React, { useState } from 'react';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { ShareField } from '@/components/dialogs/ShareDialog/ShareField';
import { AdminBrokerGroupPicker } from '../shared/pickers/AdminBrokerGroupPicker';
import { useAdminConfirm } from '../shared/AdminConfirmProvider';
import {
	useListShareableBrokerGroupsQuery,
	useShareFormFieldConfigsMutation,
} from './api/formFieldConfigsApi';

export const SHARE_FORM_FIELD_CONFIGS_DIALOG = 'shareFormFieldConfigs';

/**
 * Shares the ticked broker group configs into other broker groups, after the admin confirms:
 * each group's config for those fields is replaced with one that follows the original and
 * can't be edited there until its link is broken. Only groups on the configs' ledger that are
 * linked to their groups (a user is in both) are offered (listShareableBrokerGroups). Opened
 * with openDialog({ dialogName: SHARE_FORM_FIELD_CONFIGS_DIALOG, data: { docIds, skipped,
 * sharedWith } }) (skipped: ticked ledger or default rows left out). With sharedWith (from the
 * Shared with cell: one original and the groups it's shared with now), those groups start
 * ticked and saving makes it shared with exactly the ticked groups: unticked ones lose their
 * shared config and fall back to their ledger or the default. Mount it only while open, so
 * each opening starts from its data.
 */
export const ShareFormFieldConfigsDialog = () => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { docIds, skipped, sharedWith } = useSelector(
		(state) => state.dialog.data[SHARE_FORM_FIELD_CONFIGS_DIALOG],
	);
	const replace = Boolean(sharedWith);
	const onClose = () => dispatch(closeDialog(SHARE_FORM_FIELD_CONFIGS_DIALOG));
	const [brokerGroupIds, setBrokerGroupIds] = useState(sharedWith ?? []);
	const {
		data: shareableIds,
		isLoading: isShareableLoading,
		error: shareableError,
	} = useListShareableBrokerGroupsQuery({ docIds });
	const [shareFormFieldConfigs, { isLoading }] = useShareFormFieldConfigsMutation();

	const removedCount = (sharedWith ?? []).filter((id) => !brokerGroupIds.includes(id)).length;
	const isChanged = replace
		? brokerGroupIds.length !== sharedWith.length || removedCount > 0
		: brokerGroupIds.length > 0;

	const handleShare = async () => {
		const confirmed = await confirm({
			title: `Share ${docIds.length} field${docIds.length === 1 ? '' : 's'}?`,
			content: `This replaces each chosen broker group's settings for these fields. They'll follow every change to the original and can't be edited there until the link is broken.${
				removedCount
					? ` ${removedCount} unticked group${removedCount === 1 ? ' loses its' : 's lose their'} shared config and will follow the ledger or default again.`
					: ''
			}`,
			confirmText: 'Share',
		});
		if (!confirmed) return;

		const result = await shareFormFieldConfigs({ docIds, brokerGroupIds, replace });
		if (result.error) {
			dispatch(
				showToast({
					message:
						result.error.status === 400
							? result.error.data.error
							: "Couldn't share the config. Please try again.",
					error: true,
				}),
			);
			return;
		}
		dispatch(
			showToast({
				message: 'Shared the config',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
		onClose();
	};

	return (
		<Dialog
			open
			onClose={onClose}
			slotProps={{ paper: { sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } } } }}
		>
			<DialogTitle>{`Share ${docIds.length} config setting${docIds.length === 1 ? '' : 's'}`}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<Typography variant='body2'>
						{`Shares these settings into each chosen broker group. Only groups on the same ledger with a user in common are offered.${
							skipped
								? ` ${skipped} ticked ledger or default row${skipped === 1 ? " isn't" : "s aren't"} shared; copy ${skipped === 1 ? 'it' : 'them'} instead.`
								: ''
						}`}
					</Typography>
					{shareableError && (
						<Typography variant='body2' color='error'>
							{shareableError.data?.error ?? "Couldn't load the broker groups."}
						</Typography>
					)}
					<ShareField label='Broker groups'>
						{isShareableLoading ? (
							<CircularProgress size='1.5em' />
						) : (
							<AdminBrokerGroupPicker
								value={brokerGroupIds}
								onChange={setBrokerGroupIds}
								// The groups it's shared with now stay listed, so they can be unticked
								includeIds={[...(shareableIds ?? []), ...(sharedWith ?? [])]}
								showCheckbox
							/>
						)}
					</ShareField>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={handleShare}
					disabled={!isChanged || isLoading}
				>
					Share
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ShareFormFieldConfigsDialog.displayName = 'ShareFormFieldConfigsDialog';
